import dayjs from 'dayjs';
import { getAlpha2Code, registerLocale } from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

import {
  buildEventUrl,
  isEventIndexable,
  resolveCanonicalEventId,
  resolveShareImage,
  resolveShareTitle,
} from './eventMeta';

registerLocale(enLocale);

/**
 * schema.org wants a local wall-clock time carrying its own UTC offset. The
 * API hands us both halves separately, so derive the offset from the pair
 * rather than from a timezone database: it is exact, and it stays right
 * across DST without us tracking which side of a transition an event sits on.
 */
const toOffsetIso = (localTime?: string, utcTime?: string) => {
  if (!localTime || !utcTime) {
    return utcTime || undefined;
  }
  const local = dayjs(`${localTime.replace(/Z$/, '')}Z`);
  const utc = dayjs(utcTime);
  if (!local.isValid() || !utc.isValid()) {
    return utcTime;
  }
  const offsetMinutes = local.diff(utc, 'minute');
  // Nothing real is further out than this; treat it as bad data and use UTC.
  if (Math.abs(offsetMinutes) > 14 * 60) {
    return utcTime;
  }
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  const minutes = String(absolute % 60).padStart(2, '0');
  return `${localTime.slice(0, 19)}${sign}${hours}:${minutes}`;
};

/** Google wants ISO 3166-1 alpha-2; the API stores the country's name. */
const toCountryCode = (country?: string) =>
  country ? getAlpha2Code(country, 'en') ?? country : undefined;

const toPlainText = (html?: string) =>
  html
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || undefined;

/** A joint series is one listing, so it spans from its first event to its last. */
const seriesSpan = (events?: IEvent[]) => {
  const dated = (events ?? []).filter(
    candidate => candidate.Starts && candidate.Ends
  );
  if (dated.length === 0) {
    return undefined;
  }
  return {
    first: dated.reduce((earliest, candidate) =>
      Date.parse(candidate.Starts) < Date.parse(earliest.Starts)
        ? candidate
        : earliest
    ),
    last: dated.reduce((latest, candidate) =>
      Date.parse(candidate.Ends) > Date.parse(latest.Ends) ? candidate : latest
    ),
  };
};

const buildOffer = (
  url: string,
  tickets?: (ITicket | ITicketSummary)[]
): { offer: Record<string, unknown>; price: number } | undefined => {
  if (!tickets?.length) {
    return undefined;
  }
  const available = tickets.filter(ticket => ticket.Available !== false);
  const cheapest = [...(available.length > 0 ? available : tickets)].sort(
    (a, b) => a.SalePrice - b.SalePrice
  )[0];
  if (!cheapest) {
    return undefined;
  }
  // Only meaningful when the offer opens later; a past date is just noise.
  const ticketStarts = (cheapest as ITicket).Starts;
  const validFrom =
    ticketStarts && Date.parse(ticketStarts) > Date.now()
      ? ticketStarts
      : undefined;
  const ticketEnds = (cheapest as ITicket).Ends;
  const validThrough =
    ticketEnds && Date.parse(ticketEnds) > Date.now() ? ticketEnds : undefined;
  const price = Number((cheapest.SalePrice / 100).toFixed(2));
  return {
    offer: {
      '@type': 'Offer',
      url,
      price,
      priceCurrency: cheapest.Currency,
      availability:
        available.length > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/SoldOut',
      ...(validFrom ? { validFrom } : {}),
      ...(validThrough ? { validThrough } : {}),
    },
    price,
  };
};

interface IEventSchemaContext {
  event: IEvent;
  events?: IEvent[];
  eventSeries?: IEventSeries;
  venue?: IVenue;
  tickets?: (ITicket | ITicketSummary)[];
  talents?: ITalent[];
}

/**
 * Google Event structured data for the landing page.
 *
 * Returns undefined rather than partial markup whenever a required property
 * is missing, or the page is not publicly indexable: invalid structured data
 * earns a Search Console error, while absent structured data earns nothing.
 */
export const buildEventSchema = ({
  event,
  events,
  eventSeries,
  venue,
  tickets,
  talents,
}: IEventSchemaContext): Record<string, unknown> | undefined => {
  // Hidden and finished events are noindex, so they carry no structured data.
  if (!isEventIndexable(event, events)) {
    return undefined;
  }

  const name = resolveShareTitle(event, eventSeries);
  const span = eventSeries?.IsJointEvent ? seriesSpan(events) : undefined;
  const startDate = toOffsetIso(
    (span?.first ?? event).LocalStartTime,
    (span?.first ?? event).Starts
  );

  // name, startDate and location are Google's required set.
  if (!name || !startDate || !venue?.Name) {
    return undefined;
  }

  const url = buildEventUrl(
    resolveCanonicalEventId(event, events, eventSeries)
  );
  const endDate = toOffsetIso(
    (span?.last ?? event).LocalEndTime,
    (span?.last ?? event).Ends
  );
  const image = resolveShareImage(event, eventSeries);
  const description = toPlainText(event.Description);
  const organiserName = event.Organisation?.Name ?? event.OrganisationName;
  const offers = buildOffer(url, tickets);
  const geo =
    typeof venue.Latitude === 'number' && typeof venue.Longitude === 'number'
      ? {
          '@type': 'GeoCoordinates',
          latitude: venue.Latitude,
          longitude: venue.Longitude,
        }
      : undefined;
  /**
   * Only assert a status we actually know. Events/Get does not return the
   * cancellation flag yet, and defaulting to EventScheduled would tell Google
   * a cancelled event is going ahead.
   */
  const eventStatus =
    event.IsCancelled === true
      ? 'https://schema.org/EventCancelled'
      : event.IsCancelled === false
      ? 'https://schema.org/EventScheduled'
      : undefined;
  const performers = (talents ?? [])
    .filter(talent => talent.Name)
    .map(talent => ({ '@type': 'PerformingGroup', name: talent.Name }));

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name,
    startDate,
    ...(endDate ? { endDate } : {}),
    url,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(eventStatus ? { eventStatus } : {}),
    ...(event.Capacity > 0 ? { maximumAttendeeCapacity: event.Capacity } : {}),
    location: {
      '@type': 'Place',
      name: venue.Name,
      address: {
        '@type': 'PostalAddress',
        ...(venue.Street ? { streetAddress: venue.Street } : {}),
        ...(venue.City ? { addressLocality: venue.City } : {}),
        ...(venue.PostCode ? { postalCode: venue.PostCode } : {}),
        ...(toCountryCode(venue.Country)
          ? { addressCountry: toCountryCode(venue.Country) }
          : {}),
      },
      ...(geo ? { geo } : {}),
    },
    ...(organiserName
      ? { organizer: { '@type': 'Organization', name: organiserName } }
      : {}),
    ...(performers.length > 0 ? { performer: performers } : {}),
    ...(offers
      ? { offers: offers.offer, isAccessibleForFree: offers.price === 0 }
      : {}),
  };
};

/** `<` is escaped so the payload cannot break out of the script element. */
export const toJsonLd = (schema: Record<string, unknown>) =>
  JSON.stringify(schema).replace(/</g, '\\u003c');
